import { useEffect, useState } from "react";
import type { YagniCodeDeveloperBudgetsResponse } from "@yagni/shared";
import { ChapterCard, ChapterLede, ChapterSection } from "./chapter.js";
import { formatUsd } from "../usage/usageReport.js";

export const PERSONAL_TEAM_SPEND_LINE =
  "A personal Team's Agent spend is on Usage, on its own Team row named with its owner, and its owner sees it on their own Usage page, apart from their YAGNI Code line.";

/**
 * Settings → Spending, below the plan: the per-developer daily
 * budgets as they stand, read from the same store Usage edits. A limit is set
 * or changed on Usage, next to the spend it caps; this is where the bill's
 * reader sees them. Admin-only on the server; a member reads nothing and the
 * section says only where personal Team spend lands.
 */
export function DeveloperBudgets({
  fetchBudgets = defaultFetchBudgets,
}: {
  fetchBudgets?: () => Promise<YagniCodeDeveloperBudgetsResponse | null>;
}) {
  const [data, setData] = useState<YagniCodeDeveloperBudgetsResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchBudgets()
      .then((loaded) => {
        if (!cancelled) setData(loaded);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchBudgets]);

  return (
    <div data-testid="developer-budgets">
      <ChapterSection label="Per-developer budgets" />
      <ChapterLede>
        {/* Plain text: the Billing card's one Usage link is the managed-plan notice's. */}
        {PERSONAL_TEAM_SPEND_LINE} Set or change a developer's daily limit on Usage.
      </ChapterLede>
      {data && (
        <>
          {!data.enforced && data.budgets.length > 0 && (
            <p className="mb-3 text-[13px] text-[var(--honesty)]">
              These limits are recorded but not enforced in this workspace yet.
            </p>
          )}
          <ChapterCard className="px-4">
            {data.budgets.length === 0 ? (
              <p className="m-0 py-4 text-sm text-[var(--text-secondary)]">
                No developer has a daily limit; every developer is unlimited.
              </p>
            ) : (
              <ul className="m-0 list-none divide-y divide-[var(--border-subtle)] p-0">
                {data.budgets.map((budget) => (
                  <li key={budget.userId} className="flex items-center justify-between gap-4 py-3 text-sm">
                    <span className="min-w-0 truncate text-[var(--text-primary)]">
                      {budget.displayName ?? budget.email ?? "Former member"}
                    </span>
                    <span className="shrink-0 font-mono text-xs text-[var(--text-secondary)]">
                      {formatUsd(budget.spentTodayMillicents)} of {formatUsd(budget.dailyLimitMillicents)} today
                      {budget.overLimit ? " · reached" : ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </ChapterCard>
        </>
      )}
    </div>
  );
}

async function defaultFetchBudgets(): Promise<YagniCodeDeveloperBudgetsResponse | null> {
  const res = await fetch("/api/yagni-code-usage/budgets", { credentials: "include" });
  if (!res.ok) return null;
  return (await res.json()) as YagniCodeDeveloperBudgetsResponse;
}
